/**
 * Hook para gerenciar URLs de fotos do Oracle Storage com cache
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { oracleUrlCache } from '../services/oracleUrlCache';

interface UseOraclePhotoResult {
  url: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook para buscar URL assinada de foto de perfil de usuário
 * 
 * @param usuarioId - ID do usuário
 * @param hasStorageKey - Se o usuário tem foto no Oracle Storage
 * @param fallbackUrl - URL de fallback (foto local ou avatar padrão)
 * @returns Objeto com URL, estados de loading/error e função refetch
 */
export function useOraclePhoto(
  usuarioId: number | undefined,
  hasStorageKey: boolean,
  fallbackUrl?: string
): UseOraclePhotoResult {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ✅ useEffect SIMPLIFICADO - roda apenas quando usuarioId ou hasStorageKey mudam
  useEffect(() => {
    let isMounted = true; // Prevenir updates em componentes desmontados
    
    const fetchUrl = async () => {
      // ✅ VALIDAÇÃO: usuarioId deve ser um número válido
      if (!usuarioId || typeof usuarioId !== 'number' || isNaN(usuarioId) || usuarioId <= 0) {
        if (isMounted) {
          setUrl(fallbackUrl || '/img/avatar.svg');
          setLoading(false);
        }
        return;
      }
      
      // Se não tiver storage_key, usar fallback imediatamente
      if (!hasStorageKey) {
        if (isMounted) {
          setUrl(fallbackUrl || '/img/avatar.svg');
          setLoading(false);
        }
        return;
      }

      // Verificar cache
      const cachedUrl = oracleUrlCache.get('usuario_foto', usuarioId);
      if (cachedUrl) {
        if (isMounted) {
          setUrl(cachedUrl);
          setLoading(false);
        }
        return;
      }

      // Buscar do backend
      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          throw new Error('Token não encontrado');
        }

        const requestUrl = `/usuarios/${usuarioId}/foto`;
        const response = await api.get(requestUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const { url: signedUrl, tipo, expiraEm } = response.data;

        if (!signedUrl) {
          throw new Error('URL Oracle não retornada pelo backend');
        }

        // Salvar no cache
        oracleUrlCache.set('usuario_foto', usuarioId, signedUrl, tipo, expiraEm);

        if (isMounted) {
          setUrl(signedUrl);
          setError(null);
        }

        } catch (err: any) {
        // ✅ CORREÇÃO: Quando houver erro, usar fallback IMEDIATAMENTE sem causar loop
        const fallbackImage = fallbackUrl || '/img/avatar.svg';
        
        if (isMounted) {
          setError(null); // ✅ NÃO seta erro para evitar re-renders
          setUrl(fallbackImage);
        }
        
        // ✅ Cachear o fallback por 1 hora para evitar requisições repetidas
        const expiraEm = new Date(Date.now() + 3600000).toISOString();
        oracleUrlCache.set('usuario_foto', usuarioId, fallbackImage, 'local-fallback', expiraEm);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUrl();
    
    // Cleanup: prevenir updates em componentes desmontados
    return () => {
      isMounted = false;
    };
  }, [usuarioId, hasStorageKey]); // ✅ Apenas essas dependências essenciais

  const refetch = useCallback(async () => {
    // Nota: Para refetch, o componente precisa re-montar ou mudar usuarioId
    // O cache será limpo automaticamente quando expirar
  }, [usuarioId]);

  return {
    url,
    loading,
    error,
    refetch
  };
}

/**
 * Hook para buscar URL assinada de foto de paciente
 */
export function useOraclePhotoPaciente(
  pacienteId: number | undefined,
  hasStorageKey: boolean,
  fallbackUrl?: string
): UseOraclePhotoResult {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUrl = useCallback(async () => {
    if (!pacienteId || !hasStorageKey) {
      setUrl(fallbackUrl || '/img/avatar.svg');
      setLoading(false);
      return;
    }

    const cachedUrl = oracleUrlCache.get('paciente_foto', pacienteId);
    if (cachedUrl) {
      setUrl(cachedUrl);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      // TODO: Implementar endpoint GET /pacientes/:id/foto no backend
      const response = await api.get(`/pacientes/${pacienteId}/foto`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const { url: signedUrl, tipo, expiraEm } = response.data;

      oracleUrlCache.set('paciente_foto', pacienteId, signedUrl, tipo, expiraEm);

      setUrl(signedUrl);
      setError(null);

      console.log('[USE-ORACLE-PHOTO] ✅ URL de paciente obtida:', {
        pacienteId,
        tipo,
        expiraEm: new Date(expiraEm).toLocaleString('pt-BR')
      });

    } catch (err: any) {
      console.error('[USE-ORACLE-PHOTO] ❌ Erro ao buscar foto de paciente:', err);
      
      const errorMessage = err.response?.data?.mensagem || err.message || 'Erro ao carregar foto';
      setError(errorMessage);
      
      setUrl(fallbackUrl || '/img/avatar.svg');
      
      if (fallbackUrl) {
        oracleUrlCache.set('paciente_foto', pacienteId, fallbackUrl, 'local-fallback');
      }
    } finally {
      setLoading(false);
    }
  }, [pacienteId, hasStorageKey, fallbackUrl]);

  useEffect(() => {
    fetchUrl();
  }, [fetchUrl]);

  return {
    url,
    loading,
    error,
    refetch: fetchUrl
  };
}
